import { Box, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { reconciliationRepositoryToken } from './repository';
import { safePageError } from './ReconciliationPage';

export function ReconciliationRunDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { runId = '' } = useParams();
  const repository = useRepository(reconciliationRepositoryToken);
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refresh, setRefresh] = useState(0);
  const resource = useResource(
    (signal) => repository.getRun(runId, signal),
    [repository, runId, refresh],
    () => false,
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={safePageError(resource.error, t('reconciliation.errors.loadRun'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              backTo={paths.reconciliation}
              dataSource={dataSource}
              subtitle={`${resource.data.type} · ${resource.data.trigger}`}
              title={resource.data.id}
            />
            <Paper component="section" sx={{ p: 2 }} variant="outlined">
              <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
                {t('reconciliation.sections.runSummary')}
              </Typography>
              <MetadataList
                items={[
                  { label: t('reconciliation.fields.phase'), value: resource.data.phase },
                  {
                    label: t('reconciliation.fields.partner'),
                    value:
                      resource.data.partner === null
                        ? t('reconciliation.allPartners')
                        : `${resource.data.partner.code} · ${resource.data.partner.name}`,
                  },
                  {
                    label: t('reconciliation.fields.reason'),
                    value: resource.data.reason ?? t('common.notAvailable'),
                  },
                  {
                    label: t('reconciliation.fields.requestedBy'),
                    value: resource.data.requestedBy.displayName,
                  },
                  { label: t('reconciliation.fields.checked'), value: resource.data.checkedCount },
                  {
                    label: t('reconciliation.fields.exceptions'),
                    value: resource.data.exceptionCount,
                  },
                  {
                    label: t('reconciliation.fields.openExceptions'),
                    value: resource.data.openedExceptionCount,
                  },
                  {
                    label: t('reconciliation.fields.resolvedExceptions'),
                    value: resource.data.resolvedExceptionCount,
                  },
                  {
                    label: t('reconciliation.fields.failureCode'),
                    value: resource.data.failureCode ?? t('common.notAvailable'),
                  },
                  {
                    label: t('reconciliation.fields.failureAttempts'),
                    value: String(resource.data.failureAttemptCount),
                  },
                  {
                    label: t('reconciliation.fields.requestId'),
                    value: resource.data.publicRequestId ?? t('common.notAvailable'),
                  },
                ]}
              />
            </Paper>
            <Paper component="section" sx={{ p: 2 }} variant="outlined">
              <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
                {t('reconciliation.sections.timeline')}
              </Typography>
              <MetadataList
                items={[
                  timestampItem(
                    t('reconciliation.fields.requestedAt'),
                    resource.data.requestedAt,
                    language,
                  ),
                  timestampItem(
                    t('reconciliation.fields.processingStartedAt'),
                    resource.data.processingStartedAt,
                    language,
                  ),
                  timestampItem(
                    t('reconciliation.fields.lastHeartbeatAt'),
                    resource.data.lastHeartbeatAt,
                    language,
                  ),
                  timestampItem(
                    t('reconciliation.fields.completedAt'),
                    resource.data.completedAt,
                    language,
                  ),
                ]}
              />
            </Paper>
            <Link to={`${paths.reconciliation}?tab=exceptions`}>
              {t('reconciliation.actions.viewExceptions')}
            </Link>
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function timestampItem(label: string, value: string | null, language: TimeLanguage) {
  if (value === null) return { label, value: '—' };
  const formatted = formatTimestamp(value, 'Asia/Bangkok', language);
  return { label, value: `${formatted.display} (${formatted.originalOffset})` };
}
